import { type BTFileSystem } from "legacyComponents/FileUploadContainer.types";
import { LineItemType } from "legacyComponents/LineItemContainer.types";

import { type BTSelectItem } from "types/apiResponse/apiResponse";

import {
    type ILineItemEntityDetails,
    type ILineItemPresentationalDetails,
    type ILineItemsToInvoiceFormValues,
    LineItemAncestorEntity,
    type LineItemForInvoice,
    type LineItemsToInvoiceEntity,
    TableDataType,
} from "commonComponents/entity/invoicing/LineItemsToInvoice/LineItemsToInvoice.api.types";
import { type TaxGroupServiceListItemExtraData } from "commonComponents/financial/TaxRateSelect/TaxRateSelect.api.types";

import { type InvoiceFormat } from "handlers";

import { mapRelatedEntityLineItemsToInvoiceLineItems } from "entity/ownerInvoice/OwnerInvoice/OwnerInvoice.utility";
import {
    type IOwnerInvoiceLineItem,
    type OwnerInvoiceLineItem,
} from "entity/ownerInvoice/OwnerInvoiceLineItemContainer/OwnerInvoiceLineItemContainer.types";

export const getEntityTypeConfig = (
    entityType?: LineItemType,
    accountingIntegrationName?: string,
): {
    text: string;
    iconName: "Accounting" | "Bills" | "TimeClock" | undefined;
    dateLabel: string;
} => {
    const accountingIntegrationNameForDisplay = accountingIntegrationName || "Accounting";
    switch (entityType) {
        case LineItemType.OtherAccountingCost:
            return {
                text: `${accountingIntegrationNameForDisplay} Cost`,
                iconName: "Accounting",
                dateLabel: "Invoice date",
            };
        case LineItemType.Bill:
            return {
                text: "Bill",
                iconName: "Bills",
                dateLabel: "Invoice date",
            };
        case LineItemType.TimeCardItem:
            return {
                text: "Time Clock",
                iconName: "TimeClock",
                dateLabel: "Shift date",
            };
        default:
            return {
                text: "Entity",
                iconName: undefined,
                dateLabel: "Invoice date",
            };
    }
};

export const isLineItemRow = (
    item: LineItemForInvoice | LineItemAncestorEntity,
): item is LineItemForInvoice => {
    return item.type === TableDataType.LineItem;
};

export const isParentRow = (
    item: LineItemForInvoice | LineItemAncestorEntity,
): item is LineItemAncestorEntity => {
    return item.type === TableDataType.Parent;
};

export const isGrandparentRow = (
    item: LineItemForInvoice | LineItemAncestorEntity,
): item is LineItemAncestorEntity => {
    return item.type === TableDataType.GrandParent;
};

export const isAncestorRow = (
    item: LineItemForInvoice | LineItemAncestorEntity,
): item is LineItemAncestorEntity => {
    return item.type === TableDataType.Parent || item.type === TableDataType.GrandParent;
};

export const BLOCKED_BY_SELECTION_INVOICES_MESSAGE =
    "This allowance has been invoiced through its selection options and cannot be invoiced from the estimate";

export const ALL_BLOCKED_BY_SELECTION_INVOICES_MESSAGE =
    "These items cannot be invoiced because their selection options already have invoices";

export const isFullyInvoiced = (lineItem: LineItemForInvoice) => {
    return (
        lineItem.amountRemaining === 0 ||
        (lineItem.amountRemaining < 0 && lineItem.amountInvoiced > 0) ||
        (lineItem.amountRemaining > 0 && lineItem.amountInvoiced < 0)
    );
};

export const isInvoiceable = (lineItem: LineItemForInvoice): boolean => {
    return !isFullyInvoiced(lineItem) && !lineItem.isBlockedBySelectionInvoices;
};

export const calculateLineItemAmount = (
    lineItem: LineItemForInvoice,
    percent: number | undefined,
) => {
    return percent !== undefined
        ? (lineItem.builderCost * percent) / 100 + lineItem.builderCost
        : 0;
};

/**
 * Groups time clock line items by cost code and creates virtual parent entities for each group.
 * Updates the group property on each time clock line item to point to its cost code group.
 *
 * @param lineItems - Array of line items to process
 * @returns Array of virtual parent entities representing cost code groups
 */
export const createTimeClockCostCodeGroups = (lineItems: LineItemForInvoice[]) => {
    const costCodeGroups = new Map<number, LineItemForInvoice[]>();

    // Group time clock items by cost code
    lineItems.forEach((item) => {
        if (item.lineItemType === LineItemType.TimeCardItem) {
            if (!costCodeGroups.has(item.costCode)) {
                costCodeGroups.set(item.costCode, []);
            }
            costCodeGroups.get(item.costCode)!.push(item);
        }
    });

    // Create virtual ancestor groups by cost code
    Array.from(costCodeGroups.entries()).forEach(([costCode, items]) => {
        const firstItem = items[0];
        const totalAmount = items.reduce((sum, item) => sum + item.builderCost, 0);

        const group = new LineItemAncestorEntity(
            {
                id: `costCode-${costCode}`,
                title: "Labor", // Cost type -- but it is always labor
                totalAmount: totalAmount,
                parentId: null,
                subTitle: firstItem.costCodeTitle,
                entityType: LineItemType.TimeCardItem,
                dateToFilter: null, // Cost code groups don't have a single date
                attachedFiles: null,
                userName: null,
                hasOvertime: items.some((item) => item.parentEntity?.hasOvertime),
            },
            TableDataType.Parent,
        );

        items.forEach((item) => {
            item.group = group;
        });
    });
};

const getUniqueValuesById = (ancestors: LineItemAncestorEntity[]): LineItemAncestorEntity[] => {
    return ancestors.filter((g, index, arr) => {
        return arr.map((mapObj) => mapObj.id).indexOf(g.id) === index;
    });
};

export const getUniqueGroups = (lineItems: LineItemForInvoice[]): LineItemAncestorEntity[] => {
    const groups = lineItems
        .map((li) => li.group)
        .filter((g): g is LineItemAncestorEntity => g !== undefined);
    return getUniqueValuesById(groups);
};

export const getUniqueParents = (lineItems: LineItemForInvoice[]): LineItemAncestorEntity[] => {
    const parents = lineItems
        .map((li) => li.parentEntity)
        .filter((g): g is LineItemAncestorEntity => g !== undefined);
    return getUniqueValuesById(parents);
};

export const getUniqueGrandparents = (
    lineItems: LineItemForInvoice[],
): LineItemAncestorEntity[] => {
    const grandparents = lineItems
        .map((li) => li.grandparentEntity)
        .filter((g): g is LineItemAncestorEntity => g !== undefined);
    return getUniqueValuesById(grandparents);
};

/**
 * Maps line item entity API responses to presentational details for the UI.
 * Combines multiple entity types into a single presentational model with proper sorting.
 *
 * @param data - Array of entity responses from the API
 * @param entityTypes - Corresponding entity types for each response
 * @returns Unified presentational details for rendering the invoice modal
 */
export const mapLineItemToInvoiceEntityToPresentationalDetails = (
    data: LineItemsToInvoiceEntity[],
    entityTypes: LineItemType[],
) => {
    const dataMap = new Map<LineItemType, ILineItemEntityDetails>();
    for (let i = 0; i < data.length; i++) {
        const entityTypeValue = entityTypes[i];
        const entityDetails = {
            invalidTaxMethodsSelected: data[i].invalidTaxMethodsSelected,
            showLineItemsToOwner: data[i].showLineItemsToOwner,
            supportsAttachments: data[i].supportsAttachments,
            supportsLineItemStacking: data[i].supportsLineItemStacking,
            taxMethod: data[i].taxMethod,
            lineItems: [...data[i].lineItems],
            builderVarianceCodes: data[i].builderVarianceCodes,
            filterByVarianceCodes: data[i].filterByVarianceCodes,
        };
        dataMap.set(entityTypeValue, entityDetails);
    }
    let lineItems = data.flatMap((x) => x.lineItems);

    // Sort line items if we have multiple entity types and selected entities
    const hasMultipleEntityTypes = entityTypes.length > 1;
    if (hasMultipleEntityTypes) {
        lineItems = sortLineItemsByInitialSelection(lineItems);
    }

    const result: ILineItemPresentationalDetails = {
        dateRangeOptions: data.find((x) => x.dateRangeOptions)?.dateRangeOptions,
        hideDescriptionAndNotesCheckbox: data.every((x) => x.hideDescriptionAndNotesCheckbox),
        shouldDisplayGrandparentRows: data.every((x) => x.shouldDisplayGrandparentRows),
        shouldDisplayParentRows: data.every((x) => x.shouldDisplayParentRows),
        isCostPlusWorkflow: data.every((x) => x.isCostPlusWorkflow),
        isOpenBookJob: data.every((x) => x.isOpenBookJob),
        // Only Time Clock entities carry a bill-rate setting; consider just those so a non-timeclock
        // entity (which always reports false) doesn't suppress the popover.
        billRateEnabledForJob: data
            .filter((_x, i) => entityTypes[i] === LineItemType.TimeCardItem)
            .every((x) => x.billRateEnabledForJob),
        mappingWizardTitle: data.length === 1 ? data[0].mappingWizardTitle : undefined,
        pageSectionTitle: data.length === 1 ? data[0].pageSectionTitle : undefined,
        lineItems: lineItems,
        entityMap: dataMap,
        builderVarianceCodes: data.flatMap((x) => x.builderVarianceCodes),
        filterByVarianceCodes: data.some((x) => x.filterByVarianceCodes),
    };
    return result;
};

export const getCostPlusCombinedFlowLineItemTypes = () => [
    LineItemType.Bill,
    LineItemType.TimeCardItem,
    LineItemType.OtherAccountingCost,
];

export const isCostPlusCombinedWorkflow = (lineItemType: LineItemType): boolean => {
    const costPlusWorkflowTypes = getCostPlusCombinedFlowLineItemTypes();
    return costPlusWorkflowTypes.includes(lineItemType);
};

/**
 * Sorts line items by initial selection status.
 * Items that are initially selected appear first.
 *
 * @param lineItems - Array of line items to sort
 * @returns Sorted array of line items with initially selected items first
 */
export const sortLineItemsByInitialSelection = (
    lineItems: LineItemForInvoice[],
): LineItemForInvoice[] => {
    const selected = lineItems.filter((item) => item.isSelected);
    const notSelected = lineItems.filter((item) => !item.isSelected);
    return [...selected, ...notSelected];
};

export const handleAddLineItemsToInvoice = async (
    values: ILineItemsToInvoiceFormValues,
    entityMap: Map<LineItemType, ILineItemEntityDetails>,
    existingLineItems: IOwnerInvoiceLineItem[],
    newLineItemId: number,
    taxGroup?: BTSelectItem<TaxGroupServiceListItemExtraData>,
    onAddInvoicedAttachments?: (
        lineItems: LineItemForInvoice[],
    ) => Promise<BTFileSystem[] | undefined>,
    effectiveTaxVersion?: number,
    invoiceFormat?: InvoiceFormat,
    isOptedOutOfTaxes?: boolean,
) => {
    const updatedLineItems = [...existingLineItems];
    const newLineItems: OwnerInvoiceLineItem[] = [];
    const addedAttachments: BTFileSystem[] = [];

    // This is done for the estimates line item use case where it contains the original
    // line item type of the item before it was pushed to the estimate.
    const lineItemIdToEntityTypeMap = new Map<number, LineItemType>();
    for (const [entityType, entityDetails] of entityMap.entries()) {
        entityDetails.lineItems.forEach((lineItem) => {
            lineItemIdToEntityTypeMap.set(lineItem.lineItemId, entityType);
        });
    }

    const lineItemsByEntityType = values.lineItems.reduce(
        (groups: Map<LineItemType, LineItemForInvoice[]>, li) => {
            const liTypeGroup = lineItemIdToEntityTypeMap.get(li.lineItemId);
            if (!liTypeGroup) {
                // This should never happen, but prevent runtime errors if it does
                return groups;
            }

            const currentGroup = groups.get(liTypeGroup) ?? [];
            groups.set(liTypeGroup, [...currentGroup, li]);
            return groups;
        },
        new Map<LineItemType, LineItemForInvoice[]>(),
    );

    for (const [entityType, lineItems] of lineItemsByEntityType.entries()) {
        const entityDetails = entityMap.get(entityType);
        if (!entityDetails) {
            // This should never happen, but prevent runtime errors if it does
            continue;
        }

        if (
            entityDetails.supportsAttachments &&
            values.includeAttachments &&
            onAddInvoicedAttachments
        ) {
            const attachmentsToAddForCurrentEntityType = await onAddInvoicedAttachments(lineItems);
            if (attachmentsToAddForCurrentEntityType) {
                addedAttachments.push(...attachmentsToAddForCurrentEntityType);
            }
        }

        const includeDescriptions = values.includeDescriptions.get(entityType) ?? false;

        const [lineItemsFromRelatedEntity, newLineItemIdValue] =
            mapRelatedEntityLineItemsToInvoiceLineItems(
                lineItems,
                updatedLineItems,
                newLineItemId,
                includeDescriptions,
                entityDetails.supportsLineItemStacking,
                values.stackLineItems,
                taxGroup,
                effectiveTaxVersion,
                invoiceFormat,
                isOptedOutOfTaxes,
            );
        newLineItems.push(...lineItemsFromRelatedEntity);
        newLineItemId = newLineItemIdValue;
    }

    const ownerInvoiceLineItems = [...updatedLineItems, ...newLineItems];

    return { ownerInvoiceLineItems, addedAttachments, nextLineItemId: newLineItemId };
};
